import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";
import ModalSmsAuth, { GOOGLE_LOGIN_AUTHENTICATING_KEY } from "~/components/common/smsLogin/ModalSmsAuth";
import { EMAIL_LOGIN_AUTH_CODE } from "~/hooks/useAuthCodeFlow";
import ModalCopyLinkAuth from "~/components/common/copyLinkAuth/ModalCopyLinkAuth";
import ModalWhatsAppAuth from "~/components/common/whatsappLogin/ModalWhatsAppAuth";
import { useWhatsAppAuthContext } from "~/contexts/WhatsAppAuthContext";
import { getMe } from "~/services/meServices";
import {
	getClientBookingTransaction,
	putClientBookingConfirm,
	putClientBookingCancel,
} from "~/services/bookingTransactionService";
import { getInitials } from "~/utils/business";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import { handleError } from "~/utils/handleError";
import SpinnerLoading from "~/components/common/spinnerLoading/SpinnerLoading";
import Utils from "~/helpers/Utils";
import dayjs from "~/config/dayjs";
import i18n, { setLanguageFromBusiness } from "~/i18n";

// ─── palette ──────────────────────────────────────────────────────────────────
const C = {
	wine: "#6b2030",
	ink: "#1A1A2E",
	muted: "#6B7280",
	line: "#F0F1F8",
	bg: "#fff",
	overlay: "rgba(20,22,34,0.4)",
};

const fmtDate = (dateStr) => {
	if (!dateStr) return "";
	return dayjs(dateStr).format("DD/MM [às] HH:mm");
};

const fmtDateFull = (dateStr) => {
	if (!dateStr) return "";
	return dayjs(dateStr).format("dddd, D [de] MMMM [às] HH:mm");
};

// ─── WhatsApp auth dedicado para BookingConfirmation ─────────────────────────
// Usa showSuccessModal:true para evitar que o contexto faça history.push após auth.
// O onSuccess faz window.location.replace para recarregar a página com o usuário autenticado.
const WhatsAppAuthForBooking = () => {
	const {
		requestAuthCredential,
		showModal,
		authStatus,
		stopPolling,
		setShowModal,
		reset,
		isLoading,
	} = useWhatsAppAuthContext();

	const loginWithWhatsApp = async () => {
		await requestAuthCredential({
			showSuccessModal: true,
			redirectRoute: `${window.location.pathname}${window.location.search}`,
			onSuccess: () => window.location.replace(window.location.href),
		});
	};

	return (
		<ModalWhatsAppAuth
			open={showModal || true}
			onClose={() => { stopPolling(); setShowModal(false); reset(); }}
			status={authStatus}
			isLoading={isLoading}
			handleFinalizeAuth={() => window.location.replace(window.location.href)}
			loginWithWhatsApp={loginWithWhatsApp}
			showCustomLogin
			hideSuccessButton
		/>
	);
};

// ─── main component ───────────────────────────────────────────────────────────
const BookingConfirmationScreen = () => {
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation();

	const meStore = useSelector((store) => store.me?.me);
	const me = Utils.safeParseJSON("@prit-web/meData", meStore);

	const searchParams = new URLSearchParams(location.search);
	const decision = searchParams.get("decision"); // "confirm" | "cancel"
	const rawToken = searchParams.get("token");    // "UUID.AUTHCODE"
	const isExternalBrowser = searchParams.get("ext") === "1";
	// Parsing do token
	const dotIndex = rawToken ? rawToken.lastIndexOf(".") : -1;
	const bookingTransactionId = dotIndex > 0 ? rawToken.slice(0, dotIndex) : null;
	const authCode = dotIndex > 0 ? rawToken.slice(dotIndex + 1) : null;
	const requiresEmailLogin = authCode === EMAIL_LOGIN_AUTH_CODE;

	const isAuthInProgressRef = useRef(false);
	const actionExecutedRef = useRef(false);

	// steps: "loading" | "confirmed" | "cancelled" | "status-info" | "expired-code" | "error"
	const [step, setStep] = useState("loading");
	const [bookingData, setBookingData] = useState(null);
	const [businessId, setBusinessId] = useState(null);
	const [errorMsg, setErrorMsg] = useState("");
	const [infoStatus, setInfoStatus] = useState(null);
	// Veio do reload pós-login com Google (fluxo LOGINEMAIL)?
	const [isCompletingGoogleLogin] = useState(() => {
		try { return sessionStorage.getItem(GOOGLE_LOGIN_AUTHENTICATING_KEY) === "1"; } catch (e) { return false; }
	});

	const TERMINAL_STATUSES = ["cancelled", "refuse", "inactive", "completed", "not_attended"];

	const STATUS_INFO_CONFIG = {
		cancelled:    { titleKey: "Reservation already cancelled",  textKey: "This reservation was already cancelled" },
		completed:    { titleKey: "Reservation completed",          textKey: "This reservation is already completed" },
		refuse:       { titleKey: "status_info_title_refuse",       textKey: "status_info_text_refuse" },
		inactive:     { titleKey: "status_info_title_inactive",     textKey: "status_info_text_inactive" },
		not_attended: { titleKey: "status_info_title_not_attended", textKey: "status_info_text_not_attended" },
	};


	useEffect(() => {
		if (!bookingTransactionId || !authCode || !decision) {
			mixpanelTrackEvent("Link_Invalido_Lembretes_Whatsapp", {
				Decision: decision,
				Raw_Token: rawToken,
				Booking_Transaction_Id: bookingTransactionId,
				Auth_Code_Present: !!authCode,
			});
			setErrorMsg(t("Invalid or expired link"));
			setStep("error");
			return;
		}

		if (isAuthInProgressRef.current) return;
		isAuthInProgressRef.current = true;

		const run = async () => {
			let userData = null;
			try {

				// Aguarda useAuthCodeFlow do App.jsx concluir (seta/limpa __PRIT_CODE_LOGIN_IN_PROGRESS__)
				await new Promise((resolve) => {
					if (!window.__PRIT_CODE_LOGIN_IN_PROGRESS__) { resolve(); return; }
					const interval = setInterval(() => {
						if (!window.__PRIT_CODE_LOGIN_IN_PROGRESS__) { clearInterval(interval); resolve(); }
					}, 100);
				});

				// Verifica se o usuário está autenticado após o fluxo do App.jsx
				const currentUser = getAuth().currentUser;
				const isLoggedIn = currentUser && !currentUser.isAnonymous;

				// Se não está logado, ou está logado mas o link é LOGINEMAIL e não estamos
				// concluindo um login Google (pós-reload) → forçar modal de autenticação.
				if (!isLoggedIn || (requiresEmailLogin && !isCompletingGoogleLogin)) {
					setStep("expired-code");
					return;
				}

				const meResponse = await getMe();
				userData = meResponse?.data;
				if (userData) {
					localStorage.setItem("@prit-web/meData", JSON.stringify(userData));
					if (userData.id) localStorage.setItem("@prit-web/userId", userData.id);
				}

				// 4. Buscar dados da reserva
				const btResponse = await getClientBookingTransaction(bookingTransactionId);
				const bt = btResponse?.data;
				if (!bt) {
					setErrorMsg(t("Booking not found"));
					setStep("error");
					return;
				}

				const firstBt = bt?.bookings?.[0];
				const resolvedBusinessId = firstBt?.business?.id;
				const businessLanguage = firstBt?.business?.language;

				setBookingData(bt);
				setBusinessId(resolvedBusinessId);

				if (resolvedBusinessId) {
					localStorage.setItem("@prit-web/businessId", resolvedBusinessId);
				}

				if (businessLanguage) {
					const lang = setLanguageFromBusiness(businessLanguage);
					if (lang) i18n.changeLanguage(lang);
				}

				// 5. Executar ação
				if (actionExecutedRef.current) return;
				actionExecutedRef.current = true;

				const firstBooking = bt?.bookings?.[0];
				if (!firstBooking?.id) {
					setErrorMsg(t("Booking not found"));
					setStep("error");
					return;
				}

				const currentStatus = firstBooking.status;

				// Se o status é terminal, exibe modal informativo sem executar ação
				if (TERMINAL_STATUSES.includes(currentStatus)) {
					setInfoStatus(currentStatus);
					setStep("status-info");
					return;
				}

				if (decision === "confirm") {
					if (!firstBooking.confirmReservation) {
						await putClientBookingConfirm(firstBooking.id);
						mixpanelTrackEvent("Confirmar_Presenca_Lembrete", { Origem: "lembrete_whatsapp", Telefone_Cliente: (userData || me)?.phone }, userData || me, firstBooking?.business);
					} 
					setStep("confirmed");
				} else if (decision === "cancel") {
					if (currentStatus !== "cancelled") {
						await putClientBookingCancel(firstBooking.id, "Cancelamento solicitado pelo cliente");
						mixpanelTrackEvent("Cancelar_Agendamento", {
							Origem: "lembrete_whatsapp",
							Quem_Cancelou: "Cliente",
							Telefone_Cliente: (userData || me)?.phone,
						}, userData || me, firstBooking?.business);
					} 
					setStep("cancelled");
				} else {
					setErrorMsg(t("Invalid action"));
					setStep("error");
				}
			} catch (err) {
				console.error("BookingConfirmationScreen error:", err);
				handleError(err, "BookingConfirmationScreen", "decision", decision);
				mixpanelTrackEvent("Erro_Ao_Acessar_Lembretes", {
					Mensagem_Erro: err?.response?.data?.message || err?.message || String(err),
					Api_Path: err?.config?.url || null,
				}, userData || me);
				setErrorMsg(err?.response?.data?.message || err?.message || t("This link has expired or is not valid"));
				setStep("error");
			} finally {
				isAuthInProgressRef.current = false;
			}
		};

		run();
	}, []);

	useEffect(() => {
		if (step === "loading" || !isCompletingGoogleLogin) return;
		try { sessionStorage.removeItem(GOOGLE_LOGIN_AUTHENTICATING_KEY); } catch (e) {}
	}, [step, isCompletingGoogleLogin]);

	// ── dados para exibição ──────────────────────────────────────────────────
	const firstBooking = bookingData?.bookings?.[0];
	const businessName = firstBooking?.business?.name || "";
	const businessInitials = getInitials(businessName);
	const businessColor = firstBooking?.business?.primaryColor || C.wine;
	const serviceName = firstBooking?.service?.name || "";
	const professionalName = firstBooking?.professional?.name || "";
	const dateStr = firstBooking?.startTime || "";

	const handleAgendar = () => {
		sessionStorage.setItem("@prit-web/booking_origin", "lembrete_remarcacao");
		history.push(businessId ? `/business/${businessId}` : "/");
	};

	const handleVerReservas = () => {
		history.push(businessId ? `/reservas/${businessId}` : "/reservas"); 
	};

	// ── render: loading ──────────────────────────────────────────────────────
	if (step === "loading") {
		return (
			<SpinnerLoading
				loading
				slowMessage={isCompletingGoogleLogin ? t("We are completing your authentication") : undefined}
			/>
		);
	}

	return (
		<>
		<div style={styles.root}>
			{/* ── Fundo: resumo da reserva (ou skeleton se sem dados) ── */}
			<div style={styles.bg}>
				<div style={styles.bgContent}>
					{step === "expired-code" ? (
						<>
							<style>{`
								@keyframes prit-shimmer {
									0%   { background-position: -400px 0; }
									100% { background-position:  400px 0; }
								}
							`}</style>
							{/* Skeleton: avatar + nome */}
							<div style={styles.businessRow}>
								<div style={{ ...styles.skeletonCircle }} />
								<div style={{ ...styles.skeletonBar, width: 140, height: 20 }} />
							</div>
							{/* Skeleton: título seção */}
							<div style={{ ...styles.skeletonBar, width: 160, height: 18, marginBottom: 16 }} />
							{/* Skeleton: data */}
							<div style={{ ...styles.skeletonBar, width: "90%", height: 14, marginBottom: 8 }} />
							{/* Skeleton: serviço */}
							<div style={{ ...styles.skeletonBar, width: "60%", height: 13 }} />
						</>
					) : (
						<>
							{/* Negócio */}
							<div style={styles.businessRow}>
								<div style={{ ...styles.avatar, background: businessColor }}>
									{businessInitials}
								</div>
								<span style={styles.businessName}>{businessName}</span>
							</div>

							{/* Resumo */}
							<div style={styles.sectionTitle}>{t("Booking summary")}</div>
							{dateStr && (
								<div style={styles.dateText}>{fmtDateFull(dateStr)}</div>
							)}
							{(serviceName || professionalName) && (
								<div style={styles.serviceText}>
									{[serviceName, professionalName].filter(Boolean).join(" · ")}
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* ── Overlay ── */}
			<div style={styles.overlay}>

				{/* ── Modal: Presença confirmada ── */}
				{step === "confirmed" && (
					<div style={styles.modal}>
						<h2 style={styles.modalTitle}>{t("Presence confirmed")}</h2>
						<p style={styles.modalText}
							dangerouslySetInnerHTML={{
								__html: t("Thanks for confirming your appointment is secured", { date: fmtDate(dateStr) })
							}}
						/>
						<button
							style={{ ...styles.btn, ...styles.btnPrimary, background: businessColor, width: "100%" }}
							onClick={handleVerReservas}
						>
							{t("Close")}
						</button>
					</div>
				)}

				{/* ── Modal: Status terminal (cancelled, refuse, inactive, completed, not_attended) ── */}
				{step === "status-info" && (() => {
					const cfg = STATUS_INFO_CONFIG[infoStatus] || STATUS_INFO_CONFIG["cancelled"];
					return (
						<div style={styles.modal}>
							<h2 style={styles.modalTitle}>{t(cfg.titleKey)}</h2>
							<p style={styles.modalText}>{t(cfg.textKey)}</p>
							<div style={styles.btnRow}>
								<button
									style={{ ...styles.btn, ...styles.btnPrimary, background: businessColor }}
									onClick={handleAgendar}
								>
									{t("Schedule new")}
								</button>
								<button
									style={{ ...styles.btn, ...styles.btnOutline }}
									onClick={handleVerReservas}
								>
									{t("View reservations")}
								</button>
							</div>
						</div>
					);
				})()}

				{/* ── Modal: Reserva cancelada ── */}
				{step === "cancelled" && (
					<div style={styles.modal}>
						<h2 style={styles.modalTitle}>{t("Reservation cancelled")}</h2>
						<p style={styles.modalText}>
							{t("Your appointment has been cancelled successfully Would you like to schedule a new appointment or view your reservations")}
						</p>
						<div style={styles.btnRow}>
							<button
								style={{ ...styles.btn, ...styles.btnPrimary, background: businessColor }}
								onClick={handleAgendar}
							>
								{t("Schedule new")}
							</button>
							<button
								style={{ ...styles.btn, ...styles.btnOutline }}
								onClick={handleVerReservas}
							>
								{t("View reservations")}
							</button>
						</div>
					</div>
				)}

				{/* ── Modal: Erro ── */}
				{step === "error" && (
					<div style={styles.modal}>
						<h2 style={styles.modalTitle}>{t("Invalid link")}</h2>
						<p style={styles.modalText}>
							{errorMsg || t("This link has expired or is not valid")}
						</p>
						<button
							style={{ ...styles.btn, ...styles.btnPrimary, background: C.wine, width: "100%" }}
							onClick={() => history.push("/")}
						>
							{t("Close")}
						</button>
					</div>
				)}
			</div>
		</div>

		{step === "expired-code" && requiresEmailLogin && !isExternalBrowser && (
			<ModalSmsAuth
				open
				loginMethod="google"
				onSuccess={() => window.location.replace(window.location.href)}
				onClose={() => window.location.replace(window.location.href)}
			/>
		)}
		<ModalCopyLinkAuth open={(step === "expired-code" && !isExternalBrowser) && !requiresEmailLogin} />

		{step === "expired-code" && isExternalBrowser && (
			<WhatsAppAuthForBooking />
		)}
		</>
	);
};

// ─── estilos ──────────────────────────────────────────────────────────────────
const styles = {
	root: {
		width: "100%",
		minHeight: "100dvh",
		position: "relative",
		background: "#fff",
		fontFamily: '-apple-system,"SF Pro Display","SF Pro Text",BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif',
	},
	bg: {
		position: "absolute",
		inset: 0,
	},
	bgContent: {
		padding: "22px 20px",
		opacity: 0.45,
	},
	businessRow: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		marginBottom: 24,
	},
	avatar: {
		width: 52,
		height: 52,
		borderRadius: 999,
		color: "#fff",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 17,
		fontWeight: 700,
		flexShrink: 0,
	},
	businessName: {
		fontSize: 20,
		fontWeight: 800,
		color: C.ink,
	},
	sectionTitle: {
		fontSize: 19,
		fontWeight: 800,
		color: C.ink,
		marginBottom: 8,
	},
	dateText: {
		fontSize: 14.5,
		fontWeight: 700,
		color: C.ink,
	},
	serviceText: {
		fontSize: 13.5,
		color: C.muted,
		marginTop: 4,
	},
	overlay: {
		position: "absolute",
		inset: 0,
		background: C.overlay,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0 22px",
	},
	modal: {
		background: "#fff",
		borderRadius: 24,
		padding: "28px 24px 24px",
		width: "100%",
		maxWidth: 380,
		textAlign: "center",
		boxShadow: "0 18px 50px rgba(0,0,0,0.25)",
	},
	modalTitle: {
		fontSize: 21,
		fontWeight: 800,
		letterSpacing: "-0.3px",
		color: C.ink,
		margin: 0,
	},
	modalText: {
		fontSize: 14.5,
		color: C.muted,
		lineHeight: 1.55,
		margin: "13px 0 21px",
	},
	btnRow: {
		display: "flex",
		gap: 10,
	},
	btn: {
		flex: 1,
		borderRadius: 999,
		padding: "13px 0",
		fontSize: 14,
		fontWeight: 700,
		cursor: "pointer",
		border: "none",
		fontFamily: "inherit",
	},
	btnPrimary: {
		color: "#fff",
	},
	btnOutline: {
		background: "#fff",
		color: C.ink,
		border: `1.5px solid ${C.ink}`,
	},
	skeletonBase: {
		borderRadius: 6,
		background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
		backgroundSize: "800px 100%",
		animation: "prit-shimmer 1.4s infinite linear",
	},
	skeletonBar: {
		borderRadius: 6,
		background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
		backgroundSize: "800px 100%",
		animation: "prit-shimmer 1.4s infinite linear",
		marginBottom: 6,
	},
	skeletonCircle: {
		width: 52,
		height: 52,
		borderRadius: 999,
		background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
		backgroundSize: "800px 100%",
		animation: "prit-shimmer 1.4s infinite linear",
		flexShrink: 0,
	},
};

export default BookingConfirmationScreen;
